import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { 
  User, 
  Users, 
  Calendar, 
  Activity, 
  Mail, 
  MessageSquare,
  Clock,
  BarChart3,
  Send
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { backendClient } from '@/lib/backendClient';
import LoadingSpinner from '@/components/LoadingSpinner';

interface PractitionerData {
  id: string;
  name: string;
  email: string;
  joinDate: string;
  lastActive: string;
  status: string;
  clientCount: number;
  activeClients: number;
  totalSessions: number;
  recentSessions: number;
}

interface PractitionerDetailModalProps {
  practitioner: PractitionerData | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ClientData {
  id: string;
  client_name: string;
  email?: string;
  created_at: string;
  is_active: boolean;
}

interface SessionData {
  id: string;
  title: string;
  category_id: string;
  created_at: string;
  delivery_status: string;
  client_id?: string;
  client_name?: string;
}

interface SupportMessage {
  id: string;
  subject: string;
  message: string;
  created_at: string;
  status: string;
  admin_email: string;
}

export const PractitionerDetailModal: React.FC<PractitionerDetailModalProps> = ({
  practitioner,
  isOpen,
  onClose,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientData[]>([]);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [supportHistory, setSupportHistory] = useState<SupportMessage[]>([]);
  const [supportForm, setSupportForm] = useState({
    subject: '',
    message: ''
  });
  const [sendingSupport, setSendingSupport] = useState(false);

  useEffect(() => {
    if (practitioner && isOpen) {
      fetchPractitionerDetails();
    }
  }, [practitioner, isOpen]);

  const fetchPractitionerDetails = async () => {
    if (!practitioner) return;
    
    setLoading(true);
    try {
      // Fetch clients
      const { data: clientsData } = await backendClient
        .from('practitioner_clients')
        .select('*')
        .eq('practitioner_id', practitioner.id)
        .order('created_at', { ascending: false });

      setClients(clientsData || []);

      // Fetch sessions
      const { data: sessionsData } = await backendClient
        .from('sessions')
        .select(`
          id,
          title,
          category_id,
          created_at,
          delivery_status,
          client_id,
          practitioner_clients!inner(client_name)
        `)
        .eq('practitioner_id', practitioner.id)
        .order('created_at', { ascending: false })
        .limit(20);

      const formattedSessions = sessionsData?.map(session => ({
        ...session,
        client_name: (session as any).practitioner_clients?.client_name
      })) || [];

      setSessions(formattedSessions);

      // Fetch support history
      const { data: supportData } = await backendClient
        .from('support_messages')
        .select('*')
        .eq('practitioner_id', practitioner.id)
        .order('created_at', { ascending: false });

      setSupportHistory(supportData || []);

    } catch (error) {
      console.error('Error fetching practitioner details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendSupport = async () => {
    if (!practitioner || !supportForm.subject.trim() || !supportForm.message.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in both subject and message fields.",
        variant: "destructive",
      });
      return;
    }

    setSendingSupport(true);
    try {
      const { data, error } = await backendClient.functions.invoke('send-practitioner-support', {
        body: {
          practitionerId: practitioner.id,
          subject: supportForm.subject,
          message: supportForm.message,
        }
      });

      if (error) throw error;

      toast({
        title: "Support Message Sent",
        description: `Message sent successfully to ${practitioner.name}`,
      });

      setSupportForm({ subject: '', message: '' });
      fetchPractitionerDetails(); // Refresh support history

    } catch (error: any) {
      console.error('Error sending support message:', error);
      toast({
        title: "Error",
        description: "Failed to send support message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingSupport(false);
    }
  };

  if (!practitioner) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
              {practitioner.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                {practitioner.name}
                <Badge variant="outline">Practitioner</Badge>
              </div>
              <p className="text-sm text-muted-foreground font-normal">{practitioner.email}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" text="Loading practitioner details..." />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
              <TabsTrigger value="sessions">Sessions ({sessions.length})</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="support">Support</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Profile Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Name:</span>
                      <span className="text-sm font-medium">{practitioner.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Email:</span>
                      <span className="text-sm font-medium">{practitioner.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <Badge variant={practitioner.status === 'Active' ? 'default' : 'secondary'}>
                        {practitioner.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Joined:</span>
                      <span className="text-sm font-medium">
                        {new Date(practitioner.joinDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Last Active:</span>
                      <span className="text-sm font-medium">
                        {new Date(practitioner.lastActive).toLocaleDateString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Quick Stats
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total Clients:</span>
                      <span className="text-sm font-medium">{practitioner.clientCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Active Clients:</span>
                      <span className="text-sm font-medium">{practitioner.activeClients}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Total Sessions:</span>
                      <span className="text-sm font-medium">{practitioner.totalSessions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Recent Sessions:</span>
                      <span className="text-sm font-medium">{practitioner.recentSessions}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="clients" className="space-y-4">
              {clients.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No clients found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clients.map((client) => (
                    <Card key={client.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{client.client_name}</p>
                            <p className="text-sm text-muted-foreground">{client.email || 'No email'}</p>
                            <p className="text-xs text-muted-foreground">
                              Added {new Date(client.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Badge variant={client.is_active ? 'default' : 'secondary'}>
                            {client.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sessions" className="space-y-4">
              {sessions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No sessions found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <Card key={session.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{session.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {session.client_name ? `For: ${session.client_name}` : 'Individual session'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(session.created_at).toLocaleDateString()} • {session.category_id}
                            </p>
                          </div>
                          <Badge variant="outline">
                            {session.delivery_status}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="analytics" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Activity Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Sessions this month:</span>
                        <span className="font-medium">{practitioner.recentSessions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Client retention:</span>
                        <span className="font-medium">
                          {practitioner.clientCount > 0 
                            ? Math.round((practitioner.activeClients / practitioner.clientCount) * 100)
                            : 0}%
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Avg sessions/client:</span>
                        <span className="font-medium">
                          {practitioner.clientCount > 0 
                            ? Math.round(practitioner.totalSessions / practitioner.clientCount) 
                            : 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Platform usage:</span>
                        <span className="font-medium">Regular</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="support" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    Send Support Message
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Subject</label>
                    <Input
                      value={supportForm.subject}
                      onChange={(e) => setSupportForm(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Enter message subject..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Message</label>
                    <Textarea
                      value={supportForm.message}
                      onChange={(e) => setSupportForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="Enter your support message..."
                      rows={4}
                    />
                  </div>
                  <Button 
                    onClick={handleSendSupport}
                    disabled={sendingSupport || !supportForm.subject.trim() || !supportForm.message.trim()}
                    className="w-full"
                  >
                    {sendingSupport ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Support Message
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {supportHistory.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Support History ({supportHistory.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {supportHistory.map((msg) => (
                        <div key={msg.id} className="border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-sm">{msg.subject}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {msg.status}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {new Date(msg.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{msg.message}</p>
                          <p className="text-xs text-muted-foreground">From: {msg.admin_email}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
